/* eslint-disable security/detect-object-injection */
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { Point } from '@/types/types';
import {
  AIBACustomMetadata,
  Asset,
  BatteryLevelsEnum,
} from '@/types/asset_search';
import { AssetTypeEnum } from '@/types/enums';
import {
  AssetFillIcon,
  AssetOutlineIcon,
  VehicleFillIcon,
  VehicleOutlineIcon,
} from '../components/Shared/Icons';

const IconsOutlineAssetType = new Map();
IconsOutlineAssetType.set(AssetTypeEnum.CAR, VehicleOutlineIcon);
IconsOutlineAssetType.set(AssetTypeEnum.CUSTOM, AssetOutlineIcon);

const IconsFillAssetType = new Map();
IconsFillAssetType.set(AssetTypeEnum.CAR, VehicleFillIcon);
IconsFillAssetType.set(AssetTypeEnum.CUSTOM, AssetFillIcon);
/**
 * Sorts(DESC) an object ({ [key: string]: number }) by value or property of value and returns
 * the sorted result as a Map object to preserve the sort order.
 */
export function sortJson(obj: { [key: string]: number }): {
  [key: string]: number;
} {
  return Object.fromEntries(Object.entries(obj).sort((a, b) => b[1] - a[1]));
}

/*
 * Parse dates to be more readable
 */
export function getLocaleTime(
  date: string | number | Date | null,
): string | null {
  return date ? new Date(date).toLocaleString() : null;
}

export function getDateFormatted(
  date: string | null,
  format: string,
): string | null {
  if (!date || !dayjs(date).isValid()) return null;
  const dateParsed = dayjs(date).format(format);
  if (dateParsed === 'invalid date') return null;

  return dateParsed;
}

/*
 * Parse durations to be more readable
 */
export function getDurationTime(ms: number, t: TFunction): string | null {
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const daysms = ms % (24 * 60 * 60 * 1000);
  const hours = Math.floor(daysms / (60 * 60 * 1000));
  const hoursms = ms % (60 * 60 * 1000);
  const minutes = Math.floor(hoursms / (60 * 1000));
  const minutesms = ms % (60 * 1000);
  const seconds = Math.floor(minutesms / 1000);
  let finalString = '';
  if (days != 0) {
    finalString = `${days} ${days == 1 ? t('global.day') : t('global.days')}`;
  }
  if (days == 0 && hours != 0) {
    finalString = `${hours} ${
      hours == 1 ? t('global.hour') : t('global.hours')
    }`;
  }
  if (days == 0 && hours == 0 && minutes != 0) {
    finalString = `${minutes} ${
      minutes == 1 ? t('global.minute') : t('global.minutes')
    }`;
  }
  if (finalString == '') {
    return `${seconds} ${
      seconds == 1 ? t('global.second') : t('global.seconds')
    }`;
  }
  return finalString;
}

export function sortDateComparator(a: string, b: string) {
  const timeA = isNaN(new Date(a).getTime()) ? 0 : new Date(a).getTime();
  const timeB = isNaN(new Date(b).getTime()) ? 0 : new Date(b).getTime();

  return timeA - timeB;
}

/*
 * filter data for datatable by searchbar
 */
export function getTextMatches(
  dataToFilter: any,
  stringToSearch: string,
): boolean {
  let stringExist = false;

  for (const x in dataToFilter) {
    stringExist = String(dataToFilter[x])
      .toLowerCase()
      .replaceAll('-', '')
      .includes(stringToSearch.toLowerCase().trim().replaceAll('-', ''));
    if (stringExist) return stringExist;
  }
  return stringExist;
}

/*
 * Filter array items by date ranges based on a date field.
 * @param {string} startDate Any date format parseable by new Date()
 * @param {string} endDate Any date format parseable by new Date()
 */
export function filterArrayByDateRange(
  startDate: string,
  endDate = '',
  array: any[] = [],
  dateField = 'createdDate',
) {
  if (array && array.length === 0) return [];
  if (startDate === '' || !startDate) startDate = today();
  return array.filter((item) => {
    const dataFieldAsdate = new Date(item[dateField]);
    const startDateSelected = new Date(startDate);
    if (endDate === '') {
      return dataFieldAsdate >= startDateSelected;
    } else {
      const endDateSelected = new Date(endDate);
      return (
        dataFieldAsdate >= startDateSelected &&
        dataFieldAsdate <= endDateSelected
      );
    }
  });
}

/*
 * parse time into 12 hours format
 */
export function formatTime(timeString: string) {
  const [hourString, minute] = timeString.split(':');
  const meridiemHour = +hourString % 24;
  const hour = +hourString % 12;
  return `${hour < 10 ? `0${hour}` : hour}:${minute} ${
    meridiemHour < 12 ? 'AM' : 'PM'
  }`;
}

export function isTruthy(field: any): boolean {
  return field === 'true' || field === true;
}

export function summaryList(
  array: string[],
  count = 3,
  and = 'and',
  more = 'more',
) {
  if (array.length <= count) return array.join(', ');
  else
    return (
      array.slice(0, count).join(', ') +
      ` ${and} ${array.length - count} ${more}`
    );
}

export function toRadians(number: number) {
  return number * (Math.PI / 180);
}

export function camelToTitleCase(input: string) {
  const multipleCases = input.split(' ');
  const results: string[] = [];
  for (const s of multipleCases) {
    const res = s.replace(/([A-Z])/g, ' $1').trim();
    results.push(res.charAt(0).toUpperCase() + res.slice(1));
  }
  return results.join(' ');
}

export function today() {
  return dayjs().format('YYYY-MM-DD');
}

export function todayUpperSnakeCase() {
  return dayjs().format('YYYY_MM_DD_HHmm');
}

export function last24Hours() {
  return dayjs().subtract(24, 'hours').format('YYYY-MM-DD hh:mm');
}

export function yesterday() {
  return dayjs().subtract(1, 'day').format('YYYY-MM-DD');
}

export function last7Days() {
  return dayjs().subtract(7, 'day').format('YYYY-MM-DD');
}

export function last30Days() {
  return dayjs().subtract(30, 'day').format('YYYY-MM-DD');
}

export function getExportFileName(siteId?: string, title = 'data') {
  return `${siteId?.toLowerCase()}_${title.toLowerCase()}_${todayUpperSnakeCase()}`;
}

export function distanceBetweenTwoPoints(point1: Point, point2: Point) {
  const R = 2.093e7; // Earth radius in foot
  const deltaLats = toRadians(point2.lat - point1.lat);
  const deltaLons = toRadians(point2.lon - point1.lon);
  const a =
    Math.sin(deltaLats / 2) * Math.sin(deltaLats / 2) +
    Math.cos(toRadians(point1.lat)) *
      Math.cos(toRadians(point2.lat)) *
      Math.sin(deltaLons / 2) *
      Math.sin(deltaLons / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d;
}

export function getIconByAssetType(
  assetType: AssetTypeEnum = AssetTypeEnum.CAR,
  fill = false,
): any {
  if (fill) return IconsFillAssetType.get(assetType);
  else return IconsOutlineAssetType.get(assetType);
}

export function sortBatteryLevelComparator(
  a: BatteryLevelsEnum,
  b: BatteryLevelsEnum,
) {
  const weight = {
    [BatteryLevelsEnum.EXTREMELY_LOW]: 1,
    [BatteryLevelsEnum.LOW]: 2,
    [BatteryLevelsEnum.NORMAL]: 3,
    [BatteryLevelsEnum.NO_VOLTAGE_AVAILABLE]: 4,
  };

  return (weight[a] ?? 0) - (weight[b] ?? 0);
}

export function filterForDataTable(
  items: any,
  search: string,
  siteId: string,
): Asset[] | undefined {
  if (search === '') return [];
  search = search.trim();
  const searchArr = search?.split(/[\ ,]+/);

  return items?.filter(function (item: any) {
    //Check the hard filters first
    const shouldReturn = [];

    //Check comma separate search constraints
    if (searchArr?.length > 0) {
      if (searchArr.includes('')) {
        shouldReturn.push(true);
      } else {
        let oneMatch = false;
        for (const s of searchArr) {
          const mappedItem = {
            ...item,
          };
          // Add here any fields that require a particular mapping in order to be searcheable as a string
          if (item?.dtc)
            mappedItem.dtc = item.dtc?.map((dtc: any) => dtc.code).join(',');

          if (getTextMatches(Object.values(mappedItem), String(s))) {
            oneMatch = true;
          }
          if (siteId === 'aibalpc' && s.includes('aiba_')) {
            oneMatch = true;
          }
        }
        if (!oneMatch) {
          shouldReturn.push(false);
        }
      }
    }

    return !shouldReturn.includes(false);
  });
}

export function formatFreeDays(freeDaysInt: number) {
  let freeDays = freeDaysInt.toString();
  if (freeDays.length < 2) {
    freeDays = `0${freeDays}`;
  }
  return freeDays;
}

export function getStorageDays(timeSinceAssoc: string, freeDays: number) {
  if (!timeSinceAssoc) return '';

  const daysSinceAssoc = Math.floor(
    parseFloat(timeSinceAssoc.toString() ?? '') / (24 * 60 * 60 * 1000),
  );

  const storageDays = daysSinceAssoc - freeDays;

  if (storageDays < 0) {
    return '00';
  }

  let storageDaysStr = storageDays.toString();

  if (storageDaysStr.length < 2) {
    storageDaysStr = `0${storageDaysStr}`;
  }

  return storageDaysStr;
}

export function setupAssetDerivedFields(response: Asset[]) {
  return response.map((asset) => ({
    storageDays: getStorageDays(
      asset?.time_since_assoc?.toString() ?? '',
      (asset?.custom_metadata as AIBACustomMetadata)?.freeDays ?? 0,
    ),
    ...asset,
    ...asset.custom_metadata,
  }));
}

/**
 * returns string truncated to 17 chars if initial string is 17-20 chars
 * used to remove manf. specific variations from US VIN
 * see https://github.com/mikebuss/MTBBarcodeScanner/issues/27
 * @param {string} vin
 */
export function to17CharVin(vin: string): string {
  switch (vin.length) {
    case 18:
      return vin.slice(1);

    case 19:
      return vin.slice(1, 18);

    case 20:
      return vin.slice(2, 19);

    default:
      return vin;
  }
}

/**
 * return true if and only if valid US vin
 * calculation based on length & checksum:
 * https://stackoverflow.com/questions/26407015/javascript-jquery-vin-validator
 * @param {string} vin
 */
export function isValidVin(vin: string): boolean {
  return validate(to17CharVin(vin.trim().toUpperCase()));

  // source: https://en.wikipedia.org/wiki/Vehicle_identification_number#Example_Code
  function transliterate(c: string): number {
    return '0123456789.ABCDEFGH..JKLMN.P.R..STUVWXYZ'.indexOf(c) % 10;
  }

  function getCheckDigit(vin: string): string {
    const map = '0123456789X';
    const weights = '8765432X098765432';
    let sum = 0;
    for (let i = 0; i < 17; ++i) {
      sum += transliterate(vin[i]) * map.indexOf(weights[i]);
    }
    return map[sum % 11];
  }

  function validate(vin: string): boolean {
    if (vin.length !== 17) return false;
    return getCheckDigit(vin) === vin[8];
  }
}

export function isValidWorkOrder(workOrder: string): boolean {
  // A valid work order consists of letters and numbers and has a maximum length of 7 characters.
  return /^[A-Za-z0-9]{1,7}$/.test(workOrder);
}
